import { MinuteRange, ResearchState, ResearchSummary } from '../../types/bom.types';
import { ComponentPhase } from './discoveryModel';

/**
 * Copy and counts for the research wait on Supplier Discovery. Everything is
 * derived from the status poll; there is no clock here on purpose — the buyer
 * is told how much is left and roughly when it lands, never how long it ran.
 * `aheadFromOthers` is deliberately unused: research slots are per user, so
 * other buyers' work never delays this one.
 */

export type RowTone = 'running' | 'waiting' | 'retrying' | 'ready' | 'picked' | 'empty' | 'failed' | 'review';

export interface RowState {
  label: string;
  tone: RowTone;
  /** Shows a quiet spinner beside the label. */
  busy: boolean;
}

export interface RowInput {
  phase: ComponentPhase;
  researchState?: ResearchState;
  attempt?: number | null;
  maxAttempts?: number;
  supplierCount: number;
  pickedCount: number;
  matchingSupplierCount?: number;
  otherSpecCount?: number;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The one live state a list row shows, preferring the server's research state. */
export const rowStateOf = (item: RowInput): RowState => {
  const state = item.researchState;
  if (state === 'RETRYING') {
    const of = item.attempt && item.maxAttempts ? ` (${item.attempt} of ${item.maxAttempts})` : '';
    return { label: `trying again${of}`, tone: 'retrying', busy: true };
  }
  if (state === 'RESEARCHING' || (!state && item.phase === 'searching')) {
    return { label: 'researching', tone: 'running', busy: true };
  }
  if (state === 'QUEUED' || (!state && item.phase === 'queued')) {
    return { label: 'waiting', tone: 'waiting', busy: false };
  }
  if (state === 'FAILED' || item.phase === 'failed') return { label: 'failed · retry', tone: 'failed', busy: false };
  if (item.pickedCount > 0) return { label: `${item.pickedCount} picked`, tone: 'picked', busy: false };
  if (state === 'EMPTY' || item.phase === 'empty') return { label: 'none found', tone: 'empty', busy: false };
  if (item.otherSpecCount) {
    const matching = item.matchingSupplierCount ?? 0;
    const label = `${plural(matching, 'supplier')} · ${plural(item.otherSpecCount, 'other spec')}`;
    return { label, tone: matching > 0 ? 'ready' : 'review', busy: false };
  }
  if (item.supplierCount > 0) {
    return { label: plural(item.matchingSupplierCount ?? item.supplierCount, 'supplier'), tone: 'ready', busy: false };
  }
  return { label: 'review', tone: 'review', busy: false };
};

export const isResearchRunning = (summary?: ResearchSummary | null): boolean =>
  !!summary && summary.total > 0 && summary.researching + summary.waiting > 0;

export const minutesRangeText = (range: MinuteRange): string =>
  range.min === range.max ? `${range.max}` : `${range.min}–${range.max}`;

/** "10:45" in the buyer's own clock; null when the server gave no estimate. */
export const clockTimeOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export interface EtaParts {
  minutes: string;
  doneAround: string | null;
}

export const etaPartsOf = (summary?: ResearchSummary | null): EtaParts | null => {
  if (!summary?.etaMinutes) return null;
  return { minutes: minutesRangeText(summary.etaMinutes), doneAround: clockTimeOf(summary.etaAt) };
};

/** Robbie's line in the docked panel while research runs or once it lands. */
export const researchRobbieLine = (summary: ResearchSummary): string => {
  if (!isResearchRunning(summary)) return `All ${summary.total} done.`;
  const eta = etaPartsOf(summary);
  const rest = eta ? ` The rest should land in about ${eta.minutes} minutes.` : ' The rest are on their way.';
  return `${summary.done} of ${summary.total} done.${rest}`;
};

export interface DoneTally {
  total: number;
  withSuppliers: number;
  foundNone: number;
  failed: number;
}

export const doneTallyOf = (items: RowInput[]): DoneTally => ({
  total: items.length,
  withSuppliers: items.filter((i) => i.supplierCount > 0).length,
  foundNone: items.filter((i) => i.supplierCount === 0 && (i.researchState === 'EMPTY' || i.phase === 'empty')).length,
  failed: items.filter((i) => i.researchState === 'FAILED' || i.phase === 'failed').length,
});

export const doneTitleOf = (t: DoneTally): string => {
  const bits = [`All ${t.total} done`, `${t.withSuppliers} with suppliers`];
  if (t.foundNone > 0) bits.push(`${t.foundNone} found none`);
  if (t.failed > 0) bits.push(`${t.failed} failed`);
  return bits.join(' · ');
};

/** The one quiet line a still-running part's detail pane shows. */
export const detailWaitLine = (input: {
  researchState?: ResearchState;
  phase: ComponentPhase;
  attempt?: number | null;
  maxAttempts?: number;
  startsInMinutes?: MinuteRange | null;
}): string | null => {
  const { researchState: state, phase } = input;
  if (state === 'RETRYING') {
    return input.attempt && input.maxAttempts
      ? `Trying this part again, attempt ${input.attempt} of ${input.maxAttempts}.`
      : 'Trying this part again.';
  }
  if (state === 'QUEUED' || (!state && phase === 'queued')) {
    const starts = input.startsInMinutes;
    return starts && starts.max > 0
      ? `Waiting its turn. It should start in about ${minutesRangeText(starts)} minutes.`
      : "Waiting its turn. It starts as soon as one of your other parts finishes.";
  }
  if (state === 'RESEARCHING' || (!state && phase === 'searching')) {
    return "Researching suppliers for this part. They'll appear here as they land.";
  }
  return null;
};
